import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { clerkEnabled } from "@/services/clerk";
import { useMe } from "@/features/members/hooks/useMe";
import { useUpdateProfile } from "@/features/members/hooks/useProfileMutations";
import { AvatarUpload } from "@/components/AvatarUpload";
import { LinksEditor, type LinkRow } from "@/components/profile/LinksEditor";
import { MediaEditor, type MediaRow } from "@/components/profile/MediaEditor";
import { EmailChoice } from "@/components/EmailChoice";
import { FormSkeleton } from "@/components/Skeletons";
import { sectionTitleClass } from "@/lib/typography";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";

/** Own-profile editor. Estates/links/media editors are ported here from the
 * source app — the API's profile payload already accepted them. */
export default function ProfilePage() {
  const { data: me, isPending } = useMe(clerkEnabled);
  const updateProfile = useUpdateProfile();
  const member = me?.member ?? null;
  const [name, setName] = useState("");
  const [hidden, setHidden] = useState(false);
  const [location, setLocation] = useState("");
  const [bio, setBio] = useState("");
  const [ip, setIp] = useState("");
  const [directoryEmail, setDirectoryEmail] = useState("");
  const [listservEmail, setListservEmail] = useState("");
  const [links, setLinks] = useState<LinkRow[]>([]);
  const [media, setMedia] = useState<MediaRow[]>([]);

  useEffect(() => {
    if (!member) return;
    setName(member.name ?? "");
    setHidden(Boolean(member.hidden));
    setLocation(member.location ?? "");
    setBio(member.bio ?? "");
    setIp(member.ip ?? "");
    setDirectoryEmail(member.directory_email || member.email || "");
    setListservEmail(member.listserv_email ?? "");
    setLinks(member.links);
    setMedia(member.media as MediaRow[]);
  }, [member?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isPending) return <FormSkeleton />;
  if (!member) {
    return (
      <p className="text-muted-foreground mt-12 text-center">
        No member record for this account.
      </p>
    );
  }

  function save() {
    updateProfile.mutate(
      {
        name: name.trim(),
        hidden,
        location: location || undefined,
        bio: bio || undefined,
        ip: ip || undefined,
        directory_email: directoryEmail.trim(),
        listserv_email: listservEmail.trim(),
        links: links.filter((l) => l.label.trim() && l.url.trim()),
        media: media.filter((m) => m.url.trim()),
      },
      {
        onSuccess: () => toast.success("Profile saved"),
        onError: (err) => toast.error(err.message),
      },
    );
  }

  const saving = updateProfile.isPending;
  const saveButton = (
    <Button onClick={save} disabled={saving}>
      {saving && <Spinner data-slot="icon" />}
      {saving ? "Saving…" : "Save changes"}
    </Button>
  );

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl">My Profile</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            This is what other members see in the{" "}
            <Link to={`/directory/${member.id}`} className="underline">
              directory
            </Link>
            .
          </p>
        </div>
        {saveButton}
      </div>

      {/* Whether to appear at all, before the fields that say how. Its own
          card because it governs every one of them. */}
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3">
          <Label htmlFor="listed" className="text-sm font-normal">
            Show me in the member directory
          </Label>
          <Switch
            id="listed"
            checked={!hidden}
            onCheckedChange={(on) => setHidden(!on)}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className={sectionTitleClass}>Basics</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label>Photo</Label>
            <AvatarUpload
              currentUrl={member.image_url}
              name={member.name}
              source={member.image_source ?? (member.image_url ? "upload" : null)}
              accountUrl={member.account_image_url}
            />
          </div>
          {/* One box. It was two, which captured none of "J. Q. Public",
              "María del Carmen Ruiz" or "Lee Chen (Chen Lok Lee)" and asked
              everybody else the same question twice. Emails still greet by
              first name — the server derives it from this. */}
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="profile-name">Member Name</Label>
            <Input
              id="profile-name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <p className="text-muted-foreground text-xs">
              However you write it. This is what the directory shows.
            </p>
          </div>
          <div className="space-y-2">
            <Label>Geographic Location (City, State, Country)</Label>
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            {/* About the member. The artist or legacy entity is described on
                its own page — this is the one field that is about you. */}
            <Label>Bio</Label>
            <Textarea
              rows={4}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className={sectionTitleClass}>Contact</CardTitle>
          <CardDescription>
            Add another address under{" "}
            <Link to="/account" className="underline underline-offset-4">
              Account
            </Link>
            .
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <EmailChoice
            id="directory-email"
            label="Member email for AFELL directory"
            value={directoryEmail}
            onChange={setDirectoryEmail}
          />
          <EmailChoice
            id="listserv-email"
            label="If different, member email for Google Group listserv (Gmail recommended)"
            value={listservEmail}
            onChange={setListservEmail}
          />
        </CardContent>
      </Card>

      {/* No legacies here. They were editable in two places — a picker on
          this page and the entity's own form on /organizations — which is how
          somebody ends up stewarding an estate they only meant to look at.
          The list, the relationship, the invitations and the entity's own
          fields all live on the Legacies page now. */}
      <Card>
        <CardHeader>
          <CardTitle className={sectionTitleClass}>Links</CardTitle>
          <CardDescription>Website, socials, press.</CardDescription>
        </CardHeader>
        <CardContent>
          <LinksEditor value={links} onChange={setLinks} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className={sectionTitleClass}>Media</CardTitle>
          <CardDescription>
            Images, audio, or video shown on your profile (paste URLs). For video, a Vimeo or YouTube link plays in its own player.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <MediaEditor value={media} onChange={setMedia} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className={sectionTitleClass}>
            Intellectual property
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            rows={3}
            placeholder="Copyrights, trademarks, publishing rights…"
            value={ip}
            onChange={(e) => setIp(e.target.value)}
          />
        </CardContent>
      </Card>

      {/* Billing moved to its own page, reached from the identity menu. */}
      <div className="flex justify-end pb-8">{saveButton}</div>
    </div>
  );
}
